import { useMemo, useState, useCallback } from 'react'
import { NextPage } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import Header from '~/components/Header'
import Footer from '~/components/Footer'
import { useTranslation } from 'react-i18next'
import { useAccount } from 'wagmi'
import { useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import useSWR from 'swr'
import { message } from '~/components/Message'
import {
  getRegistrationStatus,
  postRegister,
  RegistrationStatus,
} from '~/apis/campaign'
import {
  StyledContainer,
  StyledContent,
  StyledBanner,
  StyledTitleWrapper,
  StyledTitle,
  StyledSubtitle,
  StyledBannerImage,
  StyledSection,
  StyledSectionTitle,
  StyledSectionSubTitle,
  StyledActionButton,
  StyledSectionLinkWrapper,
  StyledSectionLink,
} from './styled'
import BannerImage from '~/assets/images/campaign-newyear-banner.png'
import IconBgLeftCoin from '~/assets/images/campaign-bg-left-coin.svg'
import IconBgRightCoin from '~/assets/images/campaign-bg-right-coin.svg'
import IconBgLeftLogo from '~/assets/images/campaign-bg-left-logo.svg'
import IconBgRightLogo from '~/assets/images/campaign-bg-right-logo.svg'

const errorMap: Record<string, string> = {
  event_not_started: 'event_not_started',
  event_expired: 'event_expired',
  'invalid_parameter: address': 'invalid_parameter_address',
  invalid_json_body: 'invalid_json_body',
  internal_server_error: 'internal_server_error',
  event_has_registered: 'event_has_registered',
}

const NewYear: NextPage = () => {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage === 'zh' ? 'zh-cn' : 'en-us'
  const { address: account } = useAccount()
  const router = useRouter()
  const toggleWalletModal = useModalToggle(ApplicationModal.WALLET_CONNECT, {
    modalState: { from: 'campaign' },
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  const { data: registrationData, mutate } = useSWR<RegistrationStatus>(
    account ? ['getRegistrationStatus', account] : null,
    () => getRegistrationStatus(account),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  )

  const isRegistered = registrationData?.registered ?? false

  const isCampaignEnded = useMemo(() => {
    const endTime = new Date('2026-01-07T00:00:00+08:00')
    return new Date() >= endTime
  }, [])

  const handleRegister = useCallback(async () => {
    if (!account) return

    try {
      setIsSubmitting(true)
      await postRegister({ address: account })
      mutate({ address: account, registered: true }, false)
      message.success(t('campaign_newyear_registered'))
    } catch (error) {
      let errorSuffix = 'default'
      if (error?.response?.data?.error) {
        const errorMessage = error.response.data.error
        if (errorMap[errorMessage]) {
          errorSuffix = errorMap[errorMessage]
        }
      }

      const errorKey = `campaign_newyear_error_${errorSuffix}`
      message.error(t(errorKey))
    } finally {
      setIsSubmitting(false)
    }
  }, [account, mutate, t])

  const handleClickButton = () => {
    if (!account) {
      toggleWalletModal()
    } else if (!isRegistered) {
      handleRegister()
    } else {
      router.push('/instant')
    }
  }

  const buttonText = useMemo(() => {
    if (isCampaignEnded) {
      return t('campaign_newyear_ended')
    }
    if (!account) {
      return t('connect')
    }
    if (isRegistered) {
      return t('campaign_newyear_registered')
    }
    return t('campaign_newyear_register_now')
  }, [isCampaignEnded, account, isRegistered, t])

  return (
    <>
      <Head>
        <title>{t('meta_campaign_newyear_title')}</title>
        <meta
          name="description"
          content={t('meta_campaign_newyear_description')}
        />
        <meta name="keywords" content={t('meta_campaign_newyear_keywords')} />
      </Head>
      <Header />
      <StyledContainer>
        <img src={IconBgLeftCoin} className="bg-item bg-item-top-left" />
        <img src={IconBgLeftLogo} className="bg-item bg-item-bottom-left" />
        <img src={IconBgRightLogo} className="bg-item bg-item-top-right" />
        <img src={IconBgRightCoin} className="bg-item bg-item-bottom-right" />

        <StyledContent>
          <StyledBanner>
            <StyledTitleWrapper>
              <StyledTitle>{t('campaign_newyear_title')}</StyledTitle>
              <StyledSubtitle>{t('campaign_newyear_subtitle')}</StyledSubtitle>
            </StyledTitleWrapper>
            <StyledBannerImage src={BannerImage.src} alt="banner" />
          </StyledBanner>

          <StyledSection>
            <StyledSectionTitle>
              {t('campaign_newyear_daily_rewards')}
            </StyledSectionTitle>
            <StyledSectionSubTitle>
              {t('campaign_newyear_reward_1')}
            </StyledSectionSubTitle>
            <StyledSectionSubTitle>
              {t('campaign_newyear_reward_2')}
            </StyledSectionSubTitle>
          </StyledSection>

          <StyledSection>
            <StyledSectionTitle>
              {t('campaign_newyear_grand_prize')}
            </StyledSectionTitle>
            <StyledSectionSubTitle>
              {t('campaign_newyear_grand_prize_item')}
            </StyledSectionSubTitle>
          </StyledSection>

          <StyledSection>
            <StyledSectionTitle>
              {t('campaign_newyear_end_time')}
            </StyledSectionTitle>
            <StyledSectionSubTitle>
              {t('campaign_newyear_end_time_detail')}
            </StyledSectionSubTitle>
          </StyledSection>

          <StyledActionButton
            onClick={handleClickButton}
            disabled={isSubmitting || isCampaignEnded}
          >
            {buttonText}
          </StyledActionButton>

          <StyledSectionLinkWrapper>
            <StyledSectionLink
              href={`https://support.tokenlon.im/hc/${lang}/articles/44833161485076`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('campaign_newyear_detail_link')}
            </StyledSectionLink>
          </StyledSectionLinkWrapper>
        </StyledContent>
      </StyledContainer>
      <Footer footerWrapperStyle={{ marginTop: 0 }} />
    </>
  )
}

export default NewYear
